import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_LABEL, RISK_CLASS, SOURCE_LABEL, fmtTimeAgo } from "@/lib/labels";
import { Mail, Smartphone, AlertTriangle, Sparkles } from "lucide-react";

const SOURCE_ICON = { EMAIL: Mail, APP_TICKET: Smartphone, PROBLEM_REPORT: AlertTriangle, FEATURE_REQUEST: Sparkles };

export default function Signals() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = filter ? `?source=${filter}` : "";
    setLoading(true);
    api.get(`/signals${q}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>Unified Signal Inbox</div>
        <h1 className="font-display text-[24px]">All customer signals</h1>
        <p className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}>
          Email · App tickets · Problem reports · Feature requests · one queue
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {["", "EMAIL", "APP_TICKET", "PROBLEM_REPORT", "FEATURE_REQUEST"].map((s) => (
          <button key={s || "all"} data-testid={`filter-${s || "all"}`}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 rounded-md text-[12px] border ${filter === s ? "chip-selected" : ""}`}
                  style={filter === s ? {} : { background: "var(--surface)", borderColor: "var(--border)", color: "var(--text-secondary)" }}>
            {s ? SOURCE_LABEL[s] : "All"}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto" data-testid="signals-table">
        <table className="w-full min-w-[640px] text-[12.5px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Source</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Customer</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Subject</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Category</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Sentiment</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Risks</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Priority</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Received</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Case</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="py-10 text-center" style={{ color: "var(--text-muted)" }}>Loading signals…</td></tr>
            )}
            {!loading && items.map((s) => {
              const Icon = SOURCE_ICON[s.source_type] || Mail;
              return (
                <tr key={s.id} className="table-row" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`signal-row-${s.id}`}>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-1.5">
                      <Icon size={13} style={{ color: "var(--text-secondary)" }} />
                      <span className="text-[11.5px]" style={{ color: "var(--text-secondary)" }}>{SOURCE_LABEL[s.source_type]}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="font-medium">{s.customer_name}</div>
                    <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{s.customer_email}</div>
                  </td>
                  <td className="py-2.5 px-3 max-w-[360px]">
                    <div className="truncate" title={s.subject}>{s.subject}</div>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="badge b-muted">{CATEGORY_LABEL[s.category] || s.category || "—"}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    {s.sentiment === "angry" ? <span className="badge b-rev">angry</span>
                      : s.sentiment === "frustrated" ? <span className="badge b-sla">frustrated</span>
                      : <span className="badge b-muted">{s.sentiment || "neutral"}</span>}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-1">
                      {s.revenue_risk && <span className="badge b-rev">revenue</span>}
                      {s.trust_risk && <span className="badge b-rev">trust</span>}
                      {!s.revenue_risk && !s.trust_risk && <span className="badge b-healthy">none</span>}
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`badge ${s.priority === "critical" ? "b-rev" : s.priority === "high" ? "b-sla" : "b-muted"}`}>
                      {s.priority || "medium"}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>
                    {fmtTimeAgo(s.created_at_source)}
                  </td>
                  <td className="py-2.5 px-3">
                    {s.case_id ? (
                      <Link to={`/cases/${s.case_id}`} className="text-[12px] underline-offset-2 hover:underline" data-testid={`signal-case-link-${s.id}`}>
                        Open case
                      </Link>
                    ) : <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
